/**
 * Admin-web API client for the Alerts module's platform-admin endpoints
 * (014-alerts / US7). Covers:
 *   - Cross-tenant overview          (GET  /api/admin/alerts/overview)
 *   - Cross-tenant paginated history (GET  /api/admin/alerts/history)
 *   - SuperAdmin simulation controls (POST /api/admin/alerts/simulate/{start|end})
 *
 * DTO shapes mirror the C# records in
 * `Streamko.Modules.Alerts.Contracts/Dtos/` exactly.
 */
import { apiClient } from '../../../services/apiClient';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface AdminActiveAlertRow {
  alertEventId: string;
  organizationId: string;
  organizationName: string | null;
  locationId: string;
  locationName: string | null;
  alertType: string;
  startedAt: string;
  isSimulated: boolean;
  simulationAutoEndsAt: string | null;
}

export interface AdminAlertRegionGroup {
  regionId: string;
  regionName: string | null;
  alertTypes: string[];
  earliestStartedAt: string;
  affectedLocationCount: number;
  affectedOrganizationCount: number;
  isSimulated: boolean;
  simulationAutoEndsAt: string | null;
  simulationAlertEventId: string | null;
  rows: AdminActiveAlertRow[];
}

export interface AdminAlertOverviewDto {
  regions: AdminAlertRegionGroup[];
  generatedAt: string;
}

export interface AdminAlertHistoryEntry {
  alertEventId: string;
  organizationId: string;
  organizationName: string | null;
  locationId: string;
  locationName: string | null;
  regionId: string;
  regionName: string | null;
  alertType: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  playbackAction: string;
  wasWithinHours: boolean;
  isSimulated: boolean;
  simulationAutoEnded: boolean;
  simulationTriggeredByUserId: string | null;
}

export interface AdminAlertHistoryPage {
  items: AdminAlertHistoryEntry[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface AdminAlertHistoryFilter {
  regionId?: string;
  organizationId?: string;
  fromUtc?: string;
  toUtc?: string;
  isSimulated?: boolean;
  page?: number;
  pageSize?: number;
}

export interface StartSimulationRequest {
  regionId: string;
  alertType: string;
  durationMinutes: 5 | 15 | 30 | 60;
}

export interface StartSimulationResponse {
  alertEventId: string;
  startedAt: string;
  expiresAt: string;
  affectedLocationCount: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export function getAlertsOverview(): Promise<AdminAlertOverviewDto> {
  return apiClient.get<AdminAlertOverviewDto>('/admin/alerts/overview');
}

export function getAlertsHistory(filter: AdminAlertHistoryFilter): Promise<AdminAlertHistoryPage> {
  return apiClient.get<AdminAlertHistoryPage>('/admin/alerts/history', {
    regionId: filter.regionId,
    organizationId: filter.organizationId,
    fromUtc: filter.fromUtc,
    toUtc: filter.toUtc,
    isSimulated: filter.isSimulated,
    page: filter.page ?? 1,
    pageSize: filter.pageSize ?? 50,
  });
}

export function startSimulation(request: StartSimulationRequest): Promise<StartSimulationResponse> {
  return apiClient.post<StartSimulationResponse>('/admin/alerts/simulate/start', request);
}

export function endSimulation(alertEventId: string): Promise<void> {
  return apiClient.post<void>(`/admin/alerts/simulate/${alertEventId}/end`);
}

// ---------------------------------------------------------------------------
// Ukraine regions (shared picker source for history + simulation)
// ---------------------------------------------------------------------------

export interface UkraineRegionDto {
  regionId: string;
  regionName: string;
  regionEngName: string | null;
  regionType: string;
  parentRegionId: string | null;
}

export function listRegions(type?: string): Promise<UkraineRegionDto[]> {
  // When `type` is undefined, the backend returns the whole hierarchy
  // (State + District + Community + CityOrVillage + CityDistrict) — the
  // picker needs every level so customers can target a sub-district too.
  return apiClient.get<UkraineRegionDto[]>('/admin/alerts/regions', type ? { type } : undefined);
}
